import React, { useMemo, forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import {
  selectAgGridLayout,
  selectUserPreferences,
  updateUserPreferenceKey,
  saveUserPreferences,
  selectUserPreferencesLoaded
} from './redux/slices/userPreferencesSlice';
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  RowSelectionModule,
  PaginationModule,
  ColumnAutoSizeModule,
  ColumnApiModule,
  TextFilterModule, NumberFilterModule, DateFilterModule, CustomFilterModule,
  QuickFilterModule,
  CsvExportModule,
  themeQuartz,
  iconSetQuartzLight,
  ValidationModule,
  RowStyleModule,
  CellStyleModule,
  RowApiModule,
  RowAutoHeightModule,
  ClientSideRowModelApiModule,
  RenderApiModule
} from 'ag-grid-community';

// Import enterprise modules
import {
  ServerSideRowModelModule,
  FindModule, SetFilterModule, MultiFilterModule, GroupFilterModule,
  RowGroupingModule, MasterDetailModule, ColumnsToolPanelModule
} from 'ag-grid-enterprise';

// Register only the modules we actually use
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  PaginationModule,
  RowSelectionModule,
  RowApiModule,
  ClientSideRowModelApiModule,
  RenderApiModule,
  ColumnApiModule, // CRITICAL: Required for column API functions
  FindModule,
  RowGroupingModule,
  RowAutoHeightModule,
  ColumnAutoSizeModule,
  ColumnsToolPanelModule, // CRITICAL: Required for getColumnState() and applyColumnState()
  TextFilterModule, NumberFilterModule, DateFilterModule, SetFilterModule, MultiFilterModule, GroupFilterModule, CustomFilterModule,
  QuickFilterModule,
  CsvExportModule,
  ValidationModule,
  RowStyleModule, CellStyleModule, MasterDetailModule
]);

/**
 * =============================================================================
 * AG-GRID COLUMN LAYOUT PERSISTENCE - HOW TO USE ON ANY PAGE
 * =============================================================================
 *
 * This component provides built-in column layout persistence (order, width,
 * visibility, pinning) that syncs to user preferences and survives page refresh.
 *
 * ## QUICK START - Enable on Any Page (3 steps):
 *
 * 1. **Memoize all props passed to AgGridTable** (prevents grid reinitialization):
 *    ```javascript
 *    const columnDefs = useMemo(() => [...], []);
 *    const defaultColDef = useMemo(() => ({ sortable: true, ... }), []);
 *    const rowStyle = useMemo(() => ({ cursor: 'pointer' }), []);
 *    ```
 *
 * 2. **Add layout saving callbacks** (optional - for success/error messages):
 *    ```javascript
 *    const handleLayoutSaved = useCallback(() => {
 *      setAlertState({ show: true, type: 'success', message: 'Layout saved!' });
 *    }, []);
 *
 *    const handleLayoutReset = useCallback(() => {
 *      setAlertState({ show: true, type: 'info', message: 'Layout reset!' });
 *    }, []);
 *    ```
 *
 * 3. **Enable layout saving props on AgGridTable**:
 *    ```javascript
 *    <AgGridTable
 *      rowData={data}
 *      columnDefs={columnDefs}
 *      defaultColDef={defaultColDef}
 *      rowStyle={rowStyle}
 *      enableLayoutSaving={true}              // ← Enable persistence
 *      gridId="my_unique_grid_id"             // ← Unique ID for this grid
 *      showLayoutControls={true}              // ← Show Save/Reset/Columns buttons
 *      onLayoutSaved={handleLayoutSaved}      // ← Optional callback
 *      onLayoutReset={handleLayoutReset}      // ← Optional callback
 *    />
 *    ```
 *
 * ## HOW IT WORKS:
 * - Each grid is identified by `gridId` (e.g., "vcx_events_grid", "cases_grid")
 * - Layout is stored in: `userPreferences.ag_grid_layouts.{gridId}.columnState`
 * - Persisted to: Redux state → localStorage (via Redux Persist) → Backend API
 * - Automatically restores on page load/refresh
 *
 * ## FULL EXAMPLE:
 * See VCXEventsPage.jsx lines 638-665 and 1017-1039 for a complete implementation
 *
 * =============================================================================
 */

/**
 * Simplified AG-Grid table component for client-side data display
 *
 * @param {Object} props - Component props
 * @param {Array} props.rowData - Data to display in the grid
 * @param {Array} props.columnDefs - Column definitions
 * @param {Object} props.defaultColDef - Default column definition
 * @param {number} props.height - Container height in pixels (default: 400)
 * @param {Function} props.onGridReady - Grid ready callback
 * @param {Object} props.gridOptions - Additional grid options
 * @param {boolean} props.enableLayoutSaving - Enable layout persistence (default: false)
 * @param {string} props.gridId - Unique ID for saving layout (required if enableLayoutSaving=true)
 * @param {boolean} props.showLayoutControls - Show save/reset buttons (default: false)
 * @param {string} props.controlsPosition - Position of controls: 'top', 'bottom', 'none' (default: 'top')
 * @param {Function} props.onLayoutSaved - Callback after layout is saved
 * @param {Function} props.onLayoutReset - Callback after layout is reset
 * @param {Function} props.onLayoutChanged - Callback when layout changes
 */
const AgGridTable = forwardRef(({
  rowData = [],
  columnDefs = [],
  defaultColDef = {},
  height,
  loading = false,
  onGridReady,
  gridOptions = {},
  // Layout saving props
  enableLayoutSaving = false,
  gridId = null,
  showLayoutControls = false,
  controlsPosition = 'top',
  onLayoutSaved,
  onLayoutReset,
  onLayoutChanged,
  ...props
}, ref) => {
  const dispatch = useDispatch();
  const gridApiRef = useRef(null);

  // Layout saving state
  const [hasUnsavedLayout, setHasUnsavedLayout] = useState(false);
  const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState(false);
  const isApplyingLayoutRef = useRef(false); // Flag to ignore programmatic layout changes
  const initialLoadCompleteRef = useRef(false); // Flag to track if initial layout load is complete
  const savedGridLayout = useSelector((state) =>
    enableLayoutSaving && gridId ? selectAgGridLayout(gridId)(state) : null
  );
  const preferencesLoaded = useSelector(selectUserPreferencesLoaded);
  const allPreferences = useSelector(selectUserPreferences);

  // Check if we actually have a saved layout for this grid (memoized to avoid recalculation)
  const hasSavedLayout = useMemo(
    () => Boolean(savedGridLayout?.columnState && savedGridLayout.columnState.length > 0),
    [savedGridLayout]
  );

  // Helper function to get nested values using dot notation
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return '';
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : '';
    }, obj);
  };

  // Handle grid ready
  const handleGridReady = useCallback((params) => {
    gridApiRef.current = params.api;

    // Restore saved layout if available
    if (enableLayoutSaving && hasSavedLayout) {
      try {
        isApplyingLayoutRef.current = true;
        params.api.applyColumnState({
          state: savedGridLayout.columnState,
          applyOrder: true,
        });
        setTimeout(() => {
          isApplyingLayoutRef.current = false;
          initialLoadCompleteRef.current = true;
          setHasUnsavedLayout(false);
        }, 200);
      } catch (error) {
        console.warn('[AgGridTable] Failed to restore grid layout:', error);
        isApplyingLayoutRef.current = false;
        initialLoadCompleteRef.current = true;
      }
    } else {
      initialLoadCompleteRef.current = true;
    }

    if (onGridReady) onGridReady(params);
  }, [enableLayoutSaving, hasSavedLayout, savedGridLayout, onGridReady]);

  // Apply saved layout when it becomes available from Redux Persist
  useEffect(() => {
    if (!enableLayoutSaving || !gridApiRef.current || !hasSavedLayout) {
      return;
    }

    try {
      isApplyingLayoutRef.current = true;
      gridApiRef.current.applyColumnState({
        state: savedGridLayout.columnState,
        applyOrder: true,
      });
      setTimeout(() => {
        isApplyingLayoutRef.current = false;
        initialLoadCompleteRef.current = true;
        setHasUnsavedLayout(false);
      }, 200);
    } catch (error) {
      console.warn('[AgGridTable] Failed to apply saved layout:', error);
      isApplyingLayoutRef.current = false;
      initialLoadCompleteRef.current = true;
    }
  }, [enableLayoutSaving, hasSavedLayout, savedGridLayout]);

  // Re-apply saved layout when columnDefs change (e.g., when API data loads)
  // This prevents layout loss when columnDefs are updated after initial grid render
  useEffect(() => {
    if (!enableLayoutSaving || !gridApiRef.current || !hasSavedLayout || !initialLoadCompleteRef.current) {
      return;
    }

    // Wait a bit for AG Grid to process the new columnDefs
    const timeoutId = setTimeout(() => {
      try {
        isApplyingLayoutRef.current = true;
        gridApiRef.current.applyColumnState({
          state: savedGridLayout.columnState,
          applyOrder: true,
        });
        setTimeout(() => {
          isApplyingLayoutRef.current = false;
          setHasUnsavedLayout(false);
        }, 100);
      } catch (error) {
        console.warn('[AgGridTable] Failed to re-apply layout after columnDefs change:', error);
        isApplyingLayoutRef.current = false;
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [columnDefs, enableLayoutSaving, hasSavedLayout, savedGridLayout]);

  // Reset unsaved flag when saved layout is deleted
  useEffect(() => {
    if (enableLayoutSaving && !savedGridLayout) {
      setHasUnsavedLayout(false);
    }
  }, [enableLayoutSaving, savedGridLayout]);

  // Track layout changes
  const handleColumnStateChange = useCallback(() => {
    if (!enableLayoutSaving || !gridApiRef.current || !preferencesLoaded) return;
    // Ignore programmatic layout changes (reset, initial load)
    if (isApplyingLayoutRef.current) return;
    // Only track changes after initial load is complete
    if (!initialLoadCompleteRef.current) return;
    setHasUnsavedLayout(true);
    if (onLayoutChanged) onLayoutChanged();
  }, [enableLayoutSaving, preferencesLoaded, onLayoutChanged]);

  // Save layout
  const saveLayout = useCallback(() => {
    if (!gridApiRef.current || !gridId) return;

    const columnState = gridApiRef.current.getColumnState();

    // Update Redux state (this will merge with existing ag_grid_layouts)
    dispatch(updateUserPreferenceKey({
      key: 'ag_grid_layouts',
      value: { [gridId]: { columnState } },
      merge: true  // Merge with existing layouts, don't replace
    }));

    // Save to backend - send ALL current preferences to avoid partial updates
    const updatedPreferences = {
      ...allPreferences,
      ag_grid_layouts: {
        ...(allPreferences.ag_grid_layouts || {}),
        [gridId]: { columnState }
      }
    };
    dispatch(saveUserPreferences(updatedPreferences));

    setHasUnsavedLayout(false);
    if (onLayoutSaved) onLayoutSaved(columnState);
  }, [gridId, dispatch, onLayoutSaved, allPreferences]);

  // Reset layout
  const resetLayout = useCallback(() => {
    if (!gridApiRef.current || !savedGridLayout?.columnState) return;

    try {
      isApplyingLayoutRef.current = true;
      gridApiRef.current.applyColumnState({
        state: savedGridLayout.columnState,
        applyOrder: true,
      });
      setTimeout(() => { isApplyingLayoutRef.current = false; }, 100);
      setHasUnsavedLayout(false);
      if (onLayoutReset) onLayoutReset();
    } catch (error) {
      console.warn('[AgGridTable] Failed to reset layout:', error);
      isApplyingLayoutRef.current = false;
    }
  }, [savedGridLayout, onLayoutReset]);

  // Enhanced column definitions with valueGetter for nested properties
  const enhancedColumnDefs = useMemo(() => {
    return columnDefs.map(colDef => {
      const enhanced = { ...colDef };
      
      // Add valueGetter for nested field access (e.g., "patient_info_fk.first_name")
      if (colDef.field && colDef.field.includes('.') && !enhanced.valueGetter && !enhanced.cellRenderer) {
        enhanced.valueGetter = (params) => {
          const value = getNestedValue(params.data, colDef.field);
          // Return empty string for null/undefined to avoid object warnings
          return value ?? '';
        };
      }
      
      // Add valueFormatter only for simple fields without cellRenderer to prevent object warnings
      if (!enhanced.valueFormatter && !enhanced.cellRenderer && !colDef.field?.includes('.')) {
        enhanced.valueFormatter = (params) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          if (typeof params.value === 'object') {
            return JSON.stringify(params.value);
          }
          return params.value;
        };
      }
      
      return enhanced;
    });
  }, [columnDefs]);

  // Default column configuration
  const enhancedDefaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      ...defaultColDef
    };
  }, [defaultColDef]);
  
  // to use ankrTheme in an application, pass it to the theme grid option
  const ankrTheme = themeQuartz
    .withParams({
        accentColor: "#7154E8",
        browserColorScheme: "inherit",
        fontFamily: "inherit",
        headerFontSize: 14,
        wrapperBorder: false
      });

  // Grid options
  const finalGridOptions = useMemo(() => {
    return {
      // Basic configuration
      animateRows: true,
      suppressColumnMoveAnimation: true, // Prevent jarring column animations when layout is restored
      // Overlay customization - using templates for simplicity
      overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading all matching events...</span>',
      overlayNoRowsTemplate: '<span class="ag-overlay-no-rows-center">No events found. Check your filters and try again.</span>',
      // Pagination
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: [25, 50, 100],
      // Row selection
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
      },
      // Auto column resize
      autoSizeStrategy: {
        type: 'fitCellContents',
        defaultMinWidth: 100,
        defaultMaxWidth: 300
      },
      // Columns Tool Panel - enable show/hide columns
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            minWidth: 225,
            width: 225,
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
            }
          }
        ],
        position: 'right',
        defaultToolPanel: '' // Start with sidebar closed
      },
      // Enable QuickFilter
      includeHiddenColumnsInQuickFilter: true,
      // Merge with custom options
      ...gridOptions
    };
  }, [gridOptions]);

  // Toggle columns sidebar
  const toggleColumnsSidebar = useCallback(() => {
    if (!gridApiRef.current) return;
    const isVisible = gridApiRef.current.isToolPanelShowing();

    if (isVisible) {
      gridApiRef.current.closeToolPanel();
      setIsColumnsPanelOpen(false);
    } else {
      gridApiRef.current.openToolPanel('columns');
      setIsColumnsPanelOpen(true);
    }
  }, []);

  // Layout controls component
  const LayoutControls = () => {
    if (!showLayoutControls || !enableLayoutSaving) return null;

    // Check if any buttons should be shown
    const showSaveButton = hasUnsavedLayout;
    const showResetButton = hasSavedLayout && hasUnsavedLayout;

    // Don't render container if no buttons to show
    if (!showSaveButton && !showResetButton) return null;

    return (
      <div className="d-flex justify-content-end gap-2 m-2" role="group">
        {showSaveButton && (
          <button
            className="btn btn-sm btn-info"
            onClick={saveLayout}
            disabled={!gridApiRef.current}
            title="Save current column layout"
          >
            <i className="fa fa-save me-2"></i>Save table layout
          </button>
        )}
        {showResetButton && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={resetLayout}
            disabled={!gridApiRef.current}
            title="Reset to saved layout"
          >
            <i className="fa fa-undo me-2"></i>Reset
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: height ? '100%' : '80vh', width: '100%' }}>
      {controlsPosition === 'top' && <LayoutControls />}

      <AgGridReact
        ref={ref}
        {...props}
        theme={ankrTheme.withPart(iconSetQuartzLight)}
        rowData={rowData}
        columnDefs={enhancedColumnDefs}
        defaultColDef={enhancedDefaultColDef}
        gridOptions={finalGridOptions}
        loading={loading}
        onGridReady={handleGridReady}
        onColumnMoved={enableLayoutSaving ? handleColumnStateChange : props.onColumnMoved}
        onColumnResized={enableLayoutSaving ? handleColumnStateChange : props.onColumnResized}
        onColumnVisible={enableLayoutSaving ? handleColumnStateChange : props.onColumnVisible}
        onColumnPinned={enableLayoutSaving ? handleColumnStateChange : props.onColumnPinned}
      />

      {controlsPosition === 'bottom' && <LayoutControls />}
    </div>
  );
});

AgGridTable.displayName = 'AgGridTable';

export default AgGridTable;